/**
 * useNotifications — bell-icon list + mutations + SSE auto-refresh.
 *
 * Reads from /api/notifications via react-query. Subscribes to the
 * agency-level SSE channel: when a `notification_created` event lands,
 * prepends the payload directly to the matching list caches and bumps
 * `unread_count`. No refetch round-trip — and no race between the SSE
 * emit and the new row's commit.
 *
 * When `clientId` is passed the server filters to that client only.
 * Pass `null`/undefined to see everything for the account.
 */

import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { notificationsService } from "@/services/notificationsService";
import type {
  Notification,
  NotificationListResponse,
} from "@/types/notifications";
import { useAccountRealtimeSync } from "@/hooks/useAccountRealtimeSync";

export const notificationsKeys = {
  all: ["notifications"] as const,
  list: (clientId: string | null | undefined) =>
    ["notifications", "list", clientId ?? "__all__"] as const,
};

const isNotification = (value: unknown): value is Notification => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.accountId === "string" &&
    typeof v.type === "string" &&
    typeof v.title === "string"
  );
};

export function useNotifications(clientId?: string | null) {
  const queryClient = useQueryClient();
  const scope = clientId ?? null;

  const query = useQuery<NotificationListResponse>({
    queryKey: notificationsKeys.list(scope),
    queryFn: () =>
      notificationsService.list({ clientId: scope, limit: 50 }),
    staleTime: 30_000,
  });

  // Apply incoming SSE payloads directly to every cached list query.
  // Each cache key encodes its scope (`__all__` or a specific client_id)
  // — only prepend when the new notification belongs in that scope.
  const { on } = useAccountRealtimeSync();
  useEffect(() => {
    return on("notification_created", (payload) => {
      if (!isNotification(payload)) return;
      const incoming = payload;

      const caches = queryClient.getQueriesData<NotificationListResponse>({
        queryKey: notificationsKeys.all,
      });

      caches.forEach(([key, existing]) => {
        // Key shape: ["notifications", "list", scopeKey]
        const scopeKey = Array.isArray(key) ? (key[2] as string) : "__all__";
        const inScope =
          scopeKey === "__all__" || incoming.clientId === scopeKey;
        if (!inScope) return;

        if (!existing) {
          queryClient.invalidateQueries({ queryKey: key });
          return;
        }

        if (existing.notifications.some((n) => n.id === incoming.id)) return;

        queryClient.setQueryData<NotificationListResponse>(key, {
          notifications: [incoming, ...existing.notifications].slice(0, 50),
          unreadCount:
            existing.unreadCount + (incoming.readAt ? 0 : 1),
        });
      });
    });
  }, [on, queryClient]);

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });

  return {
    notifications: query.data?.notifications ?? [],
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    markRead: (id: string) => markRead.mutate(id),
    markAllRead: () => markAllRead.mutate(),
    isMarking: markRead.isPending || markAllRead.isPending,
  };
}
